// Floating overlay toolbar shown at the top of the Freehand canvas whenever
// the Edit tool is active. Currently exposes a single control — a slider
// that scales every selection handle uniformly — so users can shrink the
// dots when they cover important geometry underneath.
//
// The panel is absolutely positioned by its parent (the workspace <main>).
// Kept intentionally small: a compact pill of controls that doesn't fight
// with the orbit gesture on the empty space around it.

import React from 'react';
import { useFreehand } from '@/lib/freehandStore';
import { Move3D } from 'lucide-react';

export default function FreehandEditToolbar() {
  const tool = useFreehand((s) => s.tool);
  const handleScale = useFreehand((s) => s.handleScale);
  const setHandleScale = useFreehand((s) => s.setHandleScale);
  if (tool !== 'edit') return null;
  return (
    <div
      className="absolute top-4 left-1/2 -translate-x-1/2 z-20 studio-glass rounded-full pl-4 pr-3 py-2 flex items-center gap-3 shadow-sm border border-slate-200/70"
      data-testid="freehand-edit-toolbar"
    >
      <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
        <Move3D className="w-3.5 h-3.5 text-slate-500" /> Edit
      </div>
      <div className="h-4 w-px bg-slate-200" />
      <label className="flex items-center gap-2 text-[11px] text-slate-500 select-none">
        <span>Handles</span>
        <input
          type="range"
          min={0.25}
          max={2.5}
          step={0.05}
          value={handleScale}
          onChange={(e) => setHandleScale(Number(e.target.value))}
          className="w-40 accent-slate-900"
          data-testid="freehand-handle-scale-slider"
        />
        <span className="w-10 tabular-nums text-right text-slate-700 font-mono">
          {handleScale.toFixed(2)}×
        </span>
      </label>
      <button
        type="button"
        onClick={() => setHandleScale(1)}
        className="text-[11px] text-slate-500 hover:text-slate-900 rounded-full px-2 py-0.5 hover:bg-slate-100 transition-colors"
        data-testid="freehand-handle-scale-reset"
        title="Reset to 1.00×"
      >
        Reset
      </button>
    </div>
  );
}
